import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  PackageOpen,
  Check,
  Send,
  Ban,
  Truck,
  Package,
  Receipt,
  History,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TooltipProvider } from '@/components/ui/tooltip';
import { DetailField } from '@/components/common/DetailField';
import {
  DIALOG_TAB_TRIGGER,
  DialogEyebrow,
  SectionHeading,
} from '@/components/common/DialogPrimitives';
import { purchaseOrdersService } from '@/services/purchase-orders';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { PurchaseOrder, PurchaseOrderStatus } from '@/types/purchase-orders';
import { InventoryInwardSheet } from '../inventory/InventoryInwardSheet';

interface Props {
  po: PurchaseOrder | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const STATUS_LABEL: Record<PurchaseOrderStatus, string> = {
  DRAFT: 'Draft',
  ISSUED: 'Issued',
  PARTIALLY_RECEIVED: 'Partially received',
  RECEIVED: 'Received',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
};

const STATUS_TONE: Record<PurchaseOrderStatus, 'muted' | 'success' | 'warning' | 'destructive'> = {
  DRAFT: 'muted',
  ISSUED: 'warning',
  PARTIALLY_RECEIVED: 'warning',
  RECEIVED: 'success',
  CLOSED: 'success',
  CANCELLED: 'destructive',
};

export function PurchaseOrderDetailSheet({ po, open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [pending, setPending] = useState<PurchaseOrderStatus | null>(null);
  const [receiveOpen, setReceiveOpen] = useState(false);

  const detailQuery = useQuery({
    queryKey: ['purchase-orders', 'detail', po?.id],
    queryFn: () => purchaseOrdersService.get(po!.id),
    enabled: !!po && open,
    initialData: po ?? undefined,
    staleTime: 5_000,
  });
  const current = detailQuery.data ?? po;

  const mutation = useMutation({
    mutationFn: (status: PurchaseOrderStatus) =>
      purchaseOrdersService.updateStatus(current!.id, { status }),
    onSuccess: (_d, status) => {
      toast.success(`PO moved to ${STATUS_LABEL[status]}`);
      qc.invalidateQueries({ queryKey: ['purchase-orders'] });
      setPending(null);
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : 'Could not update status');
      setPending(null);
    },
  });

  const update = (next: PurchaseOrderStatus) => {
    setPending(next);
    mutation.mutate(next);
  };

  const status = current?.status ?? 'DRAFT';
  const totalOrdered = (current?.items ?? []).reduce((s, l) => s + Number(l.quantity), 0);
  const totalReceived = (current?.items ?? []).reduce(
    (s, l) => s + Number(l.receivedQuantity),
    0,
  );
  const outstanding = Math.max(0, totalOrdered - totalReceived);

  const canIssue = status === 'DRAFT';
  const canCancel = !['CANCELLED', 'CLOSED', 'RECEIVED'].includes(status);
  const canClose = status === 'RECEIVED';
  const canReceive =
    !!current && ['ISSUED', 'PARTIALLY_RECEIVED'].includes(status) && outstanding > 0;
  const isTerminal = ['CANCELLED', 'CLOSED'].includes(status);

  return (
    <TooltipProvider delayDuration={150}>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex h-[88vh] w-full max-w-5xl flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="px-7 pt-6 pb-5">
            <DialogTitle className="sr-only">{current?.poNumber ?? 'Purchase order'}</DialogTitle>
            {current && (
              <div className="flex items-start gap-4">
                <span
                  aria-hidden="true"
                  className="grid h-14 w-14 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 text-primary ring-1 ring-inset ring-primary/15"
                >
                  <PackageOpen className="h-6 w-6" />
                </span>

                <div className="min-w-0 flex-1 space-y-1.5">
                  <DialogEyebrow>
                    Purchase order · {fmt.relative(current.createdAt)}
                  </DialogEyebrow>
                  <h2 className="font-mono text-[18px] font-semibold leading-tight tracking-tight text-foreground">
                    {current.poNumber}
                  </h2>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge variant={STATUS_TONE[status]} className="text-[10px]">
                      {STATUS_LABEL[status]}
                    </Badge>
                    <Badge variant="muted" className="text-[10px]">
                      {current.vendor.name}
                    </Badge>
                    <Badge variant="muted" className="font-mono text-[10px]">
                      {current.vendor.code}
                    </Badge>
                    <Badge variant="muted" className="font-mono text-[10px]">
                      {current.warehouse.code}
                    </Badge>
                    {current.externalPoRef && (
                      <Badge variant="muted" className="font-mono text-[10px]">
                        ref {current.externalPoRef}
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="flex shrink-0 items-center gap-1.5">
                  {canIssue && (
                    <Button
                      size="sm"
                      onClick={() => update('ISSUED')}
                      disabled={mutation.isPending}
                    >
                      <Send className="h-3.5 w-3.5" />
                      {pending === 'ISSUED' ? 'Issuing…' : 'Issue PO'}
                    </Button>
                  )}
                  {canReceive && (
                    <Button size="sm" onClick={() => setReceiveOpen(true)}>
                      <Truck className="h-3.5 w-3.5" />
                      Receive against PO
                    </Button>
                  )}
                  {canClose && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => update('CLOSED')}
                      disabled={mutation.isPending}
                    >
                      <Check className="h-3.5 w-3.5" />
                      Close
                    </Button>
                  )}
                </div>
              </div>
            )}
          </DialogHeader>
          <Separator />

          {/* Stats strip */}
          <div className="flex items-stretch divide-x divide-border/60 border-b border-border/60 bg-muted/20 px-7">
            <Stat
              label="Total"
              value={current?.totalAmount != null ? fmt.inr(current.totalAmount) : '—'}
              accent
            />
            <Stat label="Ordered" value={fmt.number(totalOrdered)} />
            <Stat label="Received" value={fmt.number(totalReceived)} />
            <Stat
              label="Outstanding"
              value={fmt.number(outstanding)}
              accent={outstanding > 0}
              muted={outstanding === 0}
            />
            <Stat
              label="Expected"
              value={
                current?.expectedDeliveryAt ? fmt.date(current.expectedDeliveryAt) : '—'
              }
              muted
            />
          </div>

          <Tabs defaultValue="lines" className="flex flex-1 flex-col overflow-hidden">
            <div className="border-b border-border/60 bg-background px-7">
              <TabsList className="h-10 gap-2 bg-transparent p-0">
                <TabsTrigger value="lines" className={DIALOG_TAB_TRIGGER}>
                  <Package className="h-3.5 w-3.5" />
                  Lines
                </TabsTrigger>
                <TabsTrigger value="vendor" className={DIALOG_TAB_TRIGGER}>
                  <Receipt className="h-3.5 w-3.5" />
                  Vendor
                </TabsTrigger>
                <TabsTrigger value="audit" className={DIALOG_TAB_TRIGGER}>
                  <History className="h-3.5 w-3.5" />
                  Audit
                </TabsTrigger>
              </TabsList>
            </div>

            <ScrollArea className="flex-1">
              <div className="px-7 py-5">
                <TabsContent value="lines" className="m-0 space-y-3">
                  <SectionHeading
                    title="Line items"
                    hint={`${current?.items.length ?? 0} line${current?.items.length === 1 ? '' : 's'}`}
                  />
                  {!current || current.items.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-border/70 px-4 py-10 text-center text-[12.5px] text-muted-foreground">
                      No line items.
                    </div>
                  ) : (
                    <div className="overflow-hidden rounded-lg border border-border/70">
                      <div className="grid grid-cols-12 gap-3 border-b border-border/60 bg-muted/40 px-4 py-2 text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                        <div className="col-span-5">Item</div>
                        <div className="col-span-2 text-right">Ordered</div>
                        <div className="col-span-2 text-right">Received</div>
                        <div className="col-span-1 text-right">Unit ₹</div>
                        <div className="col-span-2 text-right">Line total</div>
                      </div>
                      {current.items.map((l) => {
                        const ordered = Number(l.quantity);
                        const got = Number(l.receivedQuantity);
                        const fully = got >= ordered;
                        return (
                          <div
                            key={l.id}
                            className="grid grid-cols-12 items-center gap-3 border-b border-border/60 px-4 py-2.5 text-[12.5px] last:border-0"
                          >
                            <div className="col-span-5 min-w-0">
                              <div className="truncate font-medium text-foreground">{l.name}</div>
                              <div className="font-mono text-[10.5px] text-muted-foreground">
                                {l.sku}
                              </div>
                            </div>
                            <div className="col-span-2 text-right font-mono tabular-nums">
                              {fmt.number(ordered)}
                            </div>
                            <div
                              className={cn(
                                'col-span-2 text-right font-mono tabular-nums',
                                fully ? 'text-success' : got > 0 ? 'text-warning' : 'text-muted-foreground',
                              )}
                            >
                              {fmt.number(got)}
                            </div>
                            <div className="col-span-1 text-right font-mono tabular-nums text-muted-foreground">
                              {l.unitPrice != null ? fmt.inr(Number(l.unitPrice)) : '—'}
                            </div>
                            <div className="col-span-2 text-right font-mono tabular-nums">
                              {l.lineTotal != null ? fmt.inr(Number(l.lineTotal)) : '—'}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {!isTerminal && (
                    <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border/60 pt-4">
                      {canCancel && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => update('CANCELLED')}
                          disabled={mutation.isPending}
                          className="border-destructive/30 text-destructive hover:bg-destructive/5 hover:text-destructive"
                        >
                          <Ban className="h-3.5 w-3.5" />
                          Cancel PO
                        </Button>
                      )}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="vendor" className="m-0 space-y-5">
                  <section className="space-y-3">
                    <SectionHeading title="Vendor" />
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                      <DetailField label="Name" value={current?.vendor.name} />
                      <DetailField label="Code" value={<span className="font-mono">{current?.vendor.code}</span>} />
                      <DetailField label="External ref" value={current?.externalPoRef ?? '—'} />
                    </div>
                  </section>
                  <Separator />
                  <section className="space-y-3">
                    <SectionHeading title="Receipt destination" />
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                      <DetailField label="Warehouse" value={current?.warehouse.name} />
                      <DetailField label="Warehouse code" value={<span className="font-mono">{current?.warehouse.code}</span>} />
                      <DetailField
                        label="Expected delivery"
                        value={current?.expectedDeliveryAt ? fmt.dateTime(current.expectedDeliveryAt) : '—'}
                      />
                    </div>
                  </section>
                  {current?.notes && (
                    <>
                      <Separator />
                      <section className="space-y-2">
                        <SectionHeading title="Notes" />
                        <p className="text-[13px] leading-relaxed text-foreground">{current.notes}</p>
                      </section>
                    </>
                  )}
                </TabsContent>

                <TabsContent value="audit" className="m-0 space-y-3">
                  <SectionHeading title="Audit metadata" />
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                    <DetailField label="Created" value={fmt.dateTime(current?.createdAt)} />
                    <DetailField label="Updated" value={fmt.relative(current?.updatedAt)} />
                    <DetailField
                      label="Issued"
                      value={current?.issuedAt ? fmt.dateTime(current.issuedAt) : '—'}
                    />
                    <DetailField
                      label="Closed"
                      value={current?.closedAt ? fmt.dateTime(current.closedAt) : '—'}
                    />
                    <DetailField
                      label="Cancelled"
                      value={current?.cancelledAt ? fmt.dateTime(current.cancelledAt) : '—'}
                    />
                  </div>
                </TabsContent>
              </div>
            </ScrollArea>
          </Tabs>
        </DialogContent>
      </Dialog>

      <InventoryInwardSheet
        open={receiveOpen}
        onOpenChange={setReceiveOpen}
        prefillPurchaseOrderId={current?.id}
      />
    </TooltipProvider>
  );
}

function Stat({
  label,
  value,
  accent,
  muted,
}: {
  label: string;
  value: React.ReactNode;
  accent?: boolean;
  muted?: boolean;
}) {
  return (
    <div className="flex-1 min-w-[110px] px-4 py-3.5 first:pl-0 last:pr-0">
      <div className="text-[10px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          'mt-1.5 font-mono text-[19px] leading-none tabular-nums',
          accent ? 'font-semibold text-foreground' : muted ? 'text-muted-foreground' : 'text-foreground',
        )}
      >
        {value}
      </div>
    </div>
  );
}
